import {
  useEffect,
  useId,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import type { ComboBoxState, Key } from "react-aria-components";
import type { ComboboxSelectionMode } from "./combobox.js";
import { withExplicitRemoval } from "./comboboxRemoval.js";

/**
 * Read-only (and disabled) comboboxes can be focused and navigated but not
 * mutated — react-aria blocks ordinary selection, so removal must be blocked
 * too.
 */
export const isFieldMutable = (input: HTMLInputElement | null) =>
  !(input?.disabled || input?.readOnly);

export interface ComboboxChipHighlight {
  /** The chip the arrow keys have moved to, or null when interaction is in the input. */
  highlightedKey: Key | null;
  /** Moves the highlight, e.g. when a chip is pressed. */
  highlightChip: (key: Key) => void;
  dropHighlight: () => void;
  removeKey: (key: Key) => void;
  chipsListboxId: string;
  chipId: (key: Key) => string;
  handleInputKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  handleInputKeyDownCapture: (event: KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * The chips half of {@link ComboboxChips}: which chip the arrow keys have
 * moved to, the ids that expose it through aria-activedescendant, and removal.
 *
 * The highlight is not DOM focus — that never leaves the input, so the field
 * stays a single tab stop — which is why every one of the keys involved has to
 * be handled here rather than by the browser.
 */
export function useComboboxChipHighlight(options: {
  state: ComboBoxState<object, ComboboxSelectionMode> | null;
  selectedKeys: readonly Key[];
  inputRef: RefObject<HTMLInputElement | null>;
}): ComboboxChipHighlight {
  const { state, selectedKeys, inputRef } = options;
  const chipIdBase = useId();
  const isMutable = () => isFieldMutable(inputRef.current);

  // The chip the arrow keys have highlighted, or null when interaction is in
  // the input. DOM focus stays on the input either way.
  const [highlightedKey, setHighlightedKey] = useState<Key | null>(null);

  // Drop the highlight when its chip is removed, or when the popup opens
  // (typing or ArrowDown): the popup's listbox takes over
  // aria-activedescendant.
  const isOpen = state?.isOpen ?? false;
  useEffect(() => {
    if (
      highlightedKey !== null &&
      (isOpen || !selectedKeys.includes(highlightedKey))
    ) {
      setHighlightedKey(null);
    }
  }, [highlightedKey, isOpen, selectedKeys]);

  // Stable per-key ids: positional ids would be reused after a removal,
  // leaving aria-activedescendant unchanged so the new highlight may not be
  // announced. The key's type goes in the id because `String` would merge the
  // number 1 and the string "1" into one id for aria-activedescendant to point
  // at. (A collection holding both is unsupported by react-aria itself, whose
  // own list rendering warns about the duplicate React key — this only keeps
  // the chips from being the layer that breaks.)
  const chipsListboxId = `${chipIdBase}-chips`;
  const chipId = (key: Key) =>
    `${chipIdBase}-chip-${typeof key === "number" ? "n" : "s"}-${encodeURIComponent(String(key))}`;

  // The chips area scrolls once it is full, so the arrow keys have to bring
  // what they moved to into view — DOM focus never leaves the input, so the
  // browser won't do it.
  useEffect(() => {
    if (highlightedKey === null) {
      return;
    }
    document
      .getElementById(chipId(highlightedKey))
      ?.scrollIntoView({ block: "nearest" });
    // chipId closes over a `useId` value, which never changes.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [highlightedKey]);

  const removeKey = (key: Key) => {
    if (!state || !Array.isArray(state.value) || !isMutable()) {
      return;
    }

    const remaining = state.value.filter((valueKey) => valueKey !== key);
    withExplicitRemoval(() => {
      state.setValue(remaining);
    });
    inputRef.current?.focus();
  };

  const removeHighlighted = () => {
    if (highlightedKey === null || !isMutable()) {
      return;
    }

    const index = selectedKeys.indexOf(highlightedKey);
    const remaining = selectedKeys.filter((key) => key !== highlightedKey);
    removeKey(highlightedKey);
    setHighlightedKey(
      remaining.length > 0
        ? (remaining[Math.min(index, remaining.length - 1)] ?? null)
        : null,
    );
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;

    if (highlightedKey !== null) {
      const index = selectedKeys.indexOf(highlightedKey);

      // Chords are the text field's (word jumps, select-to-start): only the
      // bare keys navigate chips.
      const isChord =
        event.shiftKey || event.ctrlKey || event.metaKey || event.altKey;

      switch (event.key) {
        case "Backspace":
        case "Delete":
          event.preventDefault();
          removeHighlighted();
          return;
        case "ArrowLeft":
          if (isChord) {
            break;
          }
          // Pinned at the first chip, like the caret at the start of a text
          // field.
          event.preventDefault();
          setHighlightedKey(selectedKeys[Math.max(0, index - 1)] ?? null);
          return;
        case "ArrowRight":
          if (isChord) {
            break;
          }
          // Past the last chip, interaction returns to the input.
          event.preventDefault();
          setHighlightedKey(selectedKeys[index + 1] ?? null);
          return;
        // Enter and Escape are handled in the capture phase below, before
        // react-aria's own keydown gets to reset the search text.
        default:
          // Any other (non-modifier) key returns interaction to the input;
          // fall through so e.g. typing still lands there.
          if (!["Shift", "Control", "Alt", "Meta", "Tab"].includes(event.key)) {
            setHighlightedKey(null);
          }
          break;
      }

      return;
    }

    // Backspace in an empty input removes the most recently selected chip,
    // mirroring familiar multi-select behavior. Delete deliberately doesn't:
    // it deletes forwards, and there is nothing after the caret.
    if (
      event.key === "Backspace" &&
      input.value === "" &&
      isMutable() &&
      Array.isArray(state?.value) &&
      state.value.length > 0
    ) {
      const remaining = state.value.slice(0, -1);
      withExplicitRemoval(() => {
        state.setValue(remaining);
      });
      return;
    }

    // ArrowLeft with the caret at the start of the input highlights the last
    // chip. The popup closes: the highlight owns aria-activedescendant.
    // Modifier chords (Shift+ArrowLeft select-to-start etc.) stay native.
    if (
      event.key === "ArrowLeft" &&
      !event.shiftKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey &&
      input.selectionStart === 0 &&
      input.selectionEnd === 0 &&
      selectedKeys.length > 0
    ) {
      event.preventDefault();
      const lastKey = selectedKeys[selectedKeys.length - 1];
      if (lastKey !== undefined) {
        highlightChip(lastKey);
      }
    }
  };

  /**
   * Ends a chip highlight on Enter or Escape.
   *
   * In the capture phase because react-aria chains its own keydown handler
   * ahead of ours on the input and runs the whole chain regardless of
   * `preventDefault`: its Enter commits and its Escape reverts, and both reset
   * the input to the committed value — silently throwing away the search the
   * user typed before stepping into the chips. Stopping propagation here keeps
   * that chain from running at all (and, for Escape, keeps a containing dialog
   * from closing on the same keystroke).
   */
  const handleInputKeyDownCapture = (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (
      highlightedKey === null ||
      (event.key !== "Enter" && event.key !== "Escape")
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setHighlightedKey(null);
  };

  // state.close() is react-stately's commitValue, which resets the typed
  // filter text; setOpen(false) closes without committing.
  const closePopupWithoutCommitting = () => {
    if (state?.isOpen) {
      state.setOpen(false);
    }
  };

  /**
   * Moving to a chip always closes the popup: the two compete for the input's
   * aria-activedescendant, and the popup's listbox wins the effect above.
   */
  const highlightChip = (key: Key) => {
    closePopupWithoutCommitting();
    setHighlightedKey(key);
  };

  return {
    highlightedKey,
    highlightChip,
    dropHighlight: () => {
      setHighlightedKey(null);
    },
    removeKey,
    chipsListboxId,
    chipId,
    handleInputKeyDown,
    handleInputKeyDownCapture,
  };
}
